import clsx from 'clsx';
import React, { FCWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import Button from '@components/atoms/button/button';
import useWindowSize from '@hooks/use-window-size';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import useOnKeyPress from '@hooks/browser-events/use-on-key-press';
import Icon from '../icon/icon';
import styles from './preview-box.module.scss';

export interface PreviewBoxProps {
  isActive?: boolean;
  title?: string;
  closePreviewBox?: () => void;
  enableClickOutside?: boolean;
  outsideClickRef?: React.MutableRefObject<HTMLDivElement>;
  isCart?: boolean;
  className?: string;
  hideArrow?: boolean;
}

const PreviewBox: FCWithChildren<PreviewBoxProps> = (props) => {
  const {
    isActive,
    title,
    closePreviewBox,
    children,
    enableClickOutside = true,
    outsideClickRef,
    isCart = false,
    className,
    hideArrow = false,
  } = props;

  const { t } = useTranslation('default-layout');
  const { isWiderThanMd } = useWindowSize();

  const container = useRef<HTMLDivElement>(null);
  const escPress = useOnKeyPress('Escape', container);

  const onClickCloseBox = () => {
    document.body.classList.remove('scroll-lock');
    closePreviewBox();
  };

  useOnClickOutside(outsideClickRef ? outsideClickRef : container, () => {
    if (enableClickOutside) {
      document.body.classList.remove('scroll-lock');
      closePreviewBox?.();
    }
  });

  useEffect(() => {
    if (escPress) {
      document.body.classList.remove('scroll-lock');
      closePreviewBox?.();
    }
  }, [escPress]);

  useEffect(() => {
    if (isActive && !isWiderThanMd) {
      document.body.classList.add('scroll-lock');
      return;
    }
  }, [isActive]);

  return (
    <div
      ref={container}
      className={clsx(
        'vc_previewBox',
        styles.vc_previewBox,
        {
          [styles['vc_previewBox--active']]: isActive,
        },
        styles[className],
        className,
      )}
      data-cy={isActive ? 'vc_previewBox__active' : 'vc_previewBox'}
    >
      {!hideArrow && (
        <div
          className={styles.vc_previewBox__arrow}
          data-cy="vc_previewBox__arrow"
        />
      )}
      <div
        className={styles.vc_previewBox__header}
        data-cy="vc_previewBox__header"
      >
        {title && (
          <div
            className={styles.vc_previewBox__title}
            data-cy="vc_previewBox__title"
          >
            {title}
          </div>
        )}
        {/* Mobile close button */}
        <Button
          disableDefaultStyling
          tabIndex={0}
          className={styles.vc_previewBox__closeBtn}
          onClick={onClickCloseBox}
          data-cy="vc_previewBox__closeBtn"
          aria-label={t('MODAL.BUTTON.CLOSE')}
        >
          <Icon
            className={styles.vc_previewBox__closeBtn__icon}
            name="cross-x"
            ariaHidden
          />
        </Button>
      </div>
      <div
        className={clsx(
          'vc_previewBox__inner',
          styles.vc_previewBox__inner,
          isCart ? styles['vc_previewBox__inner--cart'] : '',
        )}
        data-cy="vc_previewBox__inner"
      >
        {isActive ? children : null}
      </div>
    </div>
  );
};

export default PreviewBox;
